import React, { useState, useEffect } from "react";
import { Header } from "../../Header";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "./gtcustomer.css";
import { local_signage, local_url } from "../../Const";

const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => {
  if (typeof date === 'string' && date.includes('-')) {
    return date.replace(/-/g, "");
  }
  const d = dayjs(date);
  return d.format('YYYYMMDD');
};

const formatDateTime = (dateTime, formatType) => {
  if (!dateTime || dateTime === "1753-01-01T00:00:00") return "-";
  const date = new Date(dateTime);
  if (formatType === "date") {
    const year = String(date.getFullYear()).slice(-2);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1
    ).padStart(2, "0")}/${year}`;
  }
  if (formatType === "time") {
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  }
  return "-";
};

export const GTOrderMovementNew = () => {
  const today = new Date();
  const sixtyDaysAgo = new Date(today);
  sixtyDaysAgo.setDate(today.getDate() - 1);
  const [sfAreas, setSfAreas] = useState([{ SFAreaId: "ALL", SFAreaName: "ALL" }]);
  const [selectedSFAreaId, setSelectedSFAreaId] = useState("ALL");
  const userId = localStorage.getItem("userId");

  const [businessSegments, setBusinessSegments] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([{ businessVerticalCode: "ALL", businessVerticalName: "ALL" }]);
  const [selectedSegment, setSelectedSegment] = useState("MODERN");
  const [selectedVertical, setSelectedVertical] = useState("ALL");

  const [ledgerData, setLedgerData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(dayjs(sixtyDaysAgo));
  const [toDate, setToDate] = useState(dayjs(today));
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(100);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const datePickerStyle = {
    width: 170,
    margin: "0 8px"
  };

  const calculateDateLimits = (startDate) => {
    const start = dayjs(startDate);
    let maxDate = start.add(2, 'month');
    if (maxDate.isAfter(dayjs())) {
      maxDate = dayjs();
    }
    return { maxDate };
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    const maxAllowedDate = dayjs(newValue).add(2, 'month');
    if (dayjs(toDate).isAfter(maxAllowedDate)) {
      setToDate(maxAllowedDate);
    }
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const fetchBusinessSegments = async () => {
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/BizSegments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error("Failed to fetch Business Segments");
      const data = await response.json();
      const segmentsWithAll = [
        { businessSegmentCode: "ALL", businessSegmentName: "ALL", deleted: "N" },
        ...data
      ];
      setBusinessSegments(segmentsWithAll);
    } catch (error) {
      setError("Failed to fetch Business Segments. Please try again later.");
    }
  };

  const fetchBusinessVerticals = async (segment) => {
    if (!segment || segment === "ALL") {
      setBusinessVerticals([{ businessVerticalCode: "ALL", businessVerticalName: "ALL" }]);
      return;
    }
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_url}/api/BizVertical/${segment}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error("Failed to fetch Business Verticals");
      const data = await response.json();
      const verticalsWithAll = [
        { businessVerticalCode: "ALL", businessVerticalName: "ALL" },
        ...data
      ];
      setBusinessVerticals(verticalsWithAll);
      setSelectedVertical("ALL");
    } catch (error) {
      setError("Failed to fetch Business Verticals. Please try again later.");
      setBusinessVerticals([{ businessVerticalCode: "ALL", businessVerticalName: "ALL" }]);
    }
  };

  const fetchSfAreas = async () => {
    try {
      if (!selectedSegment || selectedSegment === "ALL") {
        setSfAreas([{ SFAreaId: "ALL", SFAreaName: "ALL" }]);
        return;
      }
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${selectedSegment}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error("Failed to fetch SF Areas");
      const data = await response.json();
      const areasWithAll = [
        { SFAreaId: "ALL", SFAreaName: "ALL" },
        ...data
      ];
      setSfAreas(areasWithAll);
      setSelectedSFAreaId("ALL");
    } catch (error) {
      setError("Failed to fetch SF Areas. Please try again later.");
      setSfAreas([{ SFAreaId: "ALL", SFAreaName: "ALL" }]);
    }
  };

  useEffect(() => {
    fetchBusinessSegments();
  }, []);

  useEffect(() => {
    fetchBusinessVerticals(selectedSegment);
    fetchSfAreas();
  }, [selectedSegment]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <span className="sort-icons">
          <ArrowDropUpIcon style={{ opacity: 0.3 }} />
          <ArrowDropDownIcon style={{ opacity: 0.3 }} />
        </span>
      );
    }
    return sortConfig.direction === 'ascending'
      ? <ArrowDropUpIcon />
      : <ArrowDropDownIcon />;
  };

  const formattedFromDateForAPI = formatDateForAPI(fromDate);
  const formattedToDateForAPI = formatDateForAPI(toDate);
  const verticalParam = selectedVertical || "ALL";
  const API_URL = `${local_signage}/api/v1/gtordermovementreport?SegmentCode=${selectedSegment}&VerticalCode=${verticalParam}&AreaId=${selectedSFAreaId}&FromDate=${formattedFromDateForAPI}&ToDate=${formattedToDateForAPI}`;

  const fetchLedgerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();

      const flattenedData = [];
      data.GTOrderMovement.forEach(order => {
        const itemMap = new Map();

        // Calculate Sale totals from saleitem array
        let saleTotalWeight = 0;
        if (order.saleitem && order.saleitem.length > 0) {
          order.saleitem.forEach(item => {
            saleTotalWeight += item.ItemWeight || 0;
          });
        }

        // Calculate SR totals from sritem array
        let srTotalQty = 0;
        let srTotalAmount = 0;
        let srTotalWeight = 0;
        let srPickUpDateTime = null;
        let srApproveDateTime = null;
        let srAcceptDateTime = null;

        if (order.sritem && order.sritem.length > 0) {
          order.sritem.forEach(srItem => {
            srTotalQty += srItem.qty || 0;
            srTotalAmount += srItem.SRAmount || 0;
            srTotalWeight += srItem.ItemWeight || 0;

            // Get the first non-empty datetime for SR fields
            if (!srPickUpDateTime && srItem.SRPickUpDateTime && srItem.SRPickUpDateTime !== "1753-01-01T00:00:00") {
              srPickUpDateTime = srItem.SRPickUpDateTime;
            }
            if (!srApproveDateTime && srItem.SRApproveDateTime && srItem.SRApproveDateTime !== "1753-01-01T00:00:00") {
              srApproveDateTime = srItem.SRApproveDateTime;
            }
            if (!srAcceptDateTime && srItem.SRAcceptDateTime && srItem.SRAcceptDateTime !== "1753-01-01T00:00:00") {
              srAcceptDateTime = srItem.SRAcceptDateTime;
            }
          });
        }

        // Add calculated totals to order
        order.TotalSaleWeight = saleTotalWeight;
        order.SRTotalQty = srTotalQty;
        order.SRTotalAmount = srTotalAmount;
        order.SRTotalWeight = srTotalWeight;

        // Use SR datetime from sritem if not available in order header
        if (!order.SRPickUpDateTime || order.SRPickUpDateTime === "1753-01-01T00:00:00") {
          order.SRPickUpDateTime = srPickUpDateTime || "1753-01-01T00:00:00";
        }
        if (!order.SRApproveDateTime || order.SRApproveDateTime === "1753-01-01T00:00:00") {
          order.SRApproveDateTime = srApproveDateTime || "1753-01-01T00:00:00";
        }
        if (!order.SRAcceptDateTime || order.SRAcceptDateTime === "1753-01-01T00:00:00") {
          order.SRAcceptDateTime = srAcceptDateTime || "1753-01-01T00:00:00";
        }

        if (order.saleitem && order.saleitem.length > 0) {
          order.saleitem.forEach(item => {
            const key = `${order.SaleId}_${item.BatchNumber}`;
            itemMap.set(key, {
              ...order,
              City: order.AreaName || "-",
              ItemID: item.ItemID,
              ItemName: item.ItemName,
              BatchNumber: item.BatchNumber,
              ItemQty: item.qty,
              ItemWeight: item.ItemWeight,
              ItemNos: item.ItemNos,
              ItemTotalAmount: item.TotalAmount,
              DeliveryCharge: item.DeliveryCharge,
              SRItemQty: 0,
              SRItemWeight: 0,
              SRItemAmount: 0
            });
          });
        }

        if (order.sritem && order.sritem.length > 0) {
          order.sritem.forEach(srItem => {
            const key = `${order.SaleId}_${srItem.BatchNumber}`;
            if (itemMap.has(key)) {
              const existing = itemMap.get(key);
              existing.SRItemQty = srItem.qty;
              existing.SRItemWeight = srItem.ItemWeight;
              existing.SRItemAmount = srItem.SRAmount;
            } else {
              itemMap.set(key, {
                ...order,
                City: order.AreaName || "-",
                ItemID: srItem.ItemID,
                ItemName: srItem.itemName,
                BatchNumber: srItem.BatchNumber,
                ItemQty: 0,
                ItemWeight: 0,
                ItemNos: 0,
                ItemTotalAmount: 0,
                DeliveryCharge: 0,
                SRItemQty: srItem.qty,
                SRItemWeight: srItem.ItemWeight,
                SRItemAmount: srItem.SRAmount
              });
            }
          });
        }

        itemMap.forEach(item => {
          flattenedData.push(item);
        });

        if ((!order.saleitem || order.saleitem.length === 0) && (!order.sritem || order.sritem.length === 0)) {
          flattenedData.push(order);
        }
      });

      setLedgerData(flattenedData);

      const summaryMap = new Map();
      flattenedData.forEach(item => {
        const key = item.SaleId;
        if (!summaryMap.has(key)) {
          summaryMap.set(key, {
            SaleId: item.SaleId,
            CustomerCode: item.CustomerCode,
            CustomerName: item.CustomerName,
            City: item.City,
            CreditLimit: item.CreditLimit,
            CreditDays: item.CreditDays,
            billingDateTime: item.billingDateTime,
            TotalQty: item.TotalQty,
            TotalSaleWeight: item.TotalSaleWeight,
            TotalAmount: item.TotalAmount,
            SRTotalQty: item.SRTotalQty,
            SRTotalWeight: item.SRTotalWeight,
            SRTotalAmount: item.SRTotalAmount,
            BillReceipt: item.BillReceipt,
            DeliveryDateTime: item.DeliveryDateTime,
            DeliveryApproveDateTime: item.DeliveryApproveDateTime,
            SRPickUpDateTime: item.SRPickUpDateTime,
            SRApproveDateTime: item.SRApproveDateTime,
            SRAcceptDateTime: item.SRAcceptDateTime,
            PromoCodes: item.PromoCodes,
            items: []
          });
        }
        summaryMap.get(key).items.push(item);
      });

      const summary = Array.from(summaryMap.values());
      setSummaryData(summary);
      setFilteredData(summary);
      setCurrentPage(1);
    } catch (error) {
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = summaryData.filter((item) =>
      (item.CustomerName?.toLowerCase() || "").includes(query) ||
      (item.SaleId?.toLowerCase() || "").includes(query) ||
      (item.CustomerCode?.toLowerCase() || "").includes(query)
    );

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const toggleRow = (saleId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(saleId)) {
      newExpanded.delete(saleId);
    } else {
      newExpanded.add(saleId);
    }
    setExpandedRows(newExpanded);
  };

  const exportToCSV = () => {
    const csvRows = [];

    const headers = [
      "Sr No",
      "Customer Code",
      "Customer Name",
      "City",
      "Item Name",
      "Batch Number",
      "Bill No",
      "Promo Code",
      "Credit Limit",
      "Credit Days",
      "Bill Date",
      "Bill Time",
      "Sale Qty",
      "Sale Wt. (KG)",
      "Sale Amount",
      "Sale Return Qty",
      "Sale Return Wt. (KG)",
      "Sale Return Amount",
      "Amt Receipt",
      "Balance",
      "Bill Cleared",
      "Delivery Date",
      "Delivery Time",
      "Delivery Confirm Date",
      "Delivery Confirm Time",
      "SR Pickup Date",
      "SR Pickup Time",
      "SR Approve Date",
      "SR Approve Time",
      "SR POS Accepted Date",
      "SR POS Accepted Time",
    ];
    csvRows.push(headers.join(","));

    ledgerData.forEach((item, index) => {
      const balance = (item.TotalAmount || 0) - (item.SRTotalAmount || 0) - (item.BillReceipt || 0);

      const row = [
        index + 1,
        item.CustomerCode || "-",
        item.CustomerName || "-",
        item.City || "-",
        item.ItemName || "-",
        item.BatchNumber || "-",
        item.SaleId || "-",
        item.PromoCodes || "-",
        `${item.CreditLimit?.toFixed(2) || "0.00"}`,
        item.CreditDays || "0",
        formatDateTime(item.billingDateTime, "date"),
        formatDateTime(item.billingDateTime, "time"),
        item.ItemQty || "0",
        `${item.ItemWeight || "0"}`,
        `${item.ItemTotalAmount?.toFixed(2) || "0.00"}`,
        item.SRItemQty || "0",
        `${item.SRItemWeight || "0"}`,
        `${item.SRItemAmount?.toFixed(2) || "0.00"}`,
        `${item.BillReceipt?.toFixed(2) || "0.00"}`,
        `${balance.toFixed(2)}`,
        balance <= 0 ? "Y" : "N",
        formatDateTime(item.DeliveryDateTime, "date"),
        formatDateTime(item.DeliveryDateTime, "time"),
        formatDateTime(item.DeliveryApproveDateTime, "date"),
        formatDateTime(item.DeliveryApproveDateTime, "time"),
        formatDateTime(item.SRPickUpDateTime, "date"),
        formatDateTime(item.SRPickUpDateTime, "time"),
        formatDateTime(item.SRApproveDateTime, "date"),
        formatDateTime(item.SRApproveDateTime, "time"),
        formatDateTime(item.SRAcceptDateTime, "date"),
        formatDateTime(item.SRAcceptDateTime, "time"),
      ];

      csvRows.push(row.join(","));
    });

    const csvContent = "\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "gt_order_movement_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSegmentChange = (event) => {
    setSelectedSegment(event.target.value);
  };

  const handleVerticalChange = (event) => {
    setSelectedVertical(event.target.value);
  };

  const handleSfAreaChange = (event) => {
    setSelectedSFAreaId(event.target.value);
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  const processedFilteredData = [...filteredData].map(item => ({
    ...item,
    balance: (item.TotalAmount || 0) - (item.SRTotalAmount || 0) - (item.BillReceipt || 0)
  })).sort((a, b) => {
    if (sortConfig.key) {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }

      if (sortConfig.key.includes('DateTime')) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }

      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const currentRecords = processedFilteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(processedFilteredData.length / recordsPerPage);

  const getPageNumbers = () => {
    const maxPageNumbersToShow = 5;
    const pageNumbers = [];

    if (totalPages <= maxPageNumbersToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        endPage = Math.min(4, totalPages - 1);
      }

      if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const LoadingOverlay = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255,255,255,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <CircularProgress size={60} />
    </div>
  );

  const isGTMTSegment = (code) => code === "MODERN";

  return (
    <>
      <Header />
      {loading && <LoadingOverlay />}
      <div className="gt-customer-container">
        <div className="gt-customer-controls-wrapper">
          <div className="gt-customer-date-input-container">
            <select
              className="gt-customer-dropdown"
              value={selectedSegment}
              onChange={handleSegmentChange}
              disabled={isGTMTSegment(selectedSegment)}
            >
              {businessSegments.map((segment) => (
                <option
                  key={segment.businessSegmentCode}
                  value={segment.businessSegmentCode}
                >
                  {segment.businessSegmentName}
                </option>
              ))}
            </select>

            <select
              className="gt-customer-dropdown"
              value={selectedVertical}
              onChange={handleVerticalChange}
              disabled={!selectedSegment || selectedSegment === "ALL"}
            >
              {businessVerticals.map((vertical) => (
                <option key={vertical.businessVerticalCode} value={vertical.businessVerticalCode}>
                  {vertical.businessVerticalName} {vertical.businessVerticalCode !== "ALL" ? `(${vertical.businessVerticalCode})` : ""}
                </option>
              ))}
            </select>

            <select
              className="gt-customer-dropdown"
              value={selectedSFAreaId}
              onChange={handleSfAreaChange}
            >
              {sfAreas.map((area) => (
                <option key={area.SFAreaId} value={area.SFAreaId}>
                  {area.SFAreaName} {area.SFAreaId !== "ALL" ? `(${area.SFAreaId})` : ""}
                </option>
              ))}
            </select>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={fromDate}
                onChange={handleFromDateChange}
                format="DD-MM-YYYY"
                sx={datePickerStyle}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined"
                  }
                }}
              />

              <DatePicker
                value={toDate}
                onChange={handleToDateChange}
                format="DD-MM-YYYY"
                sx={datePickerStyle}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined"
                  }
                }}
              />
            </LocalizationProvider>

            <button
              onClick={fetchLedgerData}
              className="gt-customer-submit-button"
              disabled={loading}
            >
              Submit
            </button>
            <button
              onClick={exportToCSV}
              className="gt-customer-export-button"
              disabled={loading || ledgerData.length === 0}
            >
              Export to CSV
            </button>

            <div className="gt-customer-search-pagination-container">
            <div className="gt-customer-search-container">
              <input
                type="text"
                placeholder="Search by Customer Name or Bill No"
                value={searchQuery}
                onChange={handleSearch}
                className="gt-customer-search-input"
              />


            {filteredData.length > recordsPerPage && (
              <div className="pagination-container">
                <button
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  First
                </button>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  Prev
                </button>

                {pageNumbers.map((number, index) => (
                  number === '...' ?
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span> :
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`pagination-button ${currentPage === number ? 'pagination-active' : ''}`}
                  >
                    {number}
                  </button>
                ))}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                </button>
                <button
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Last
                </button>

                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            )}
             </div>
          </div>
          </div>
        </div>

        <div className="gt-customer-table-container">
          <div className="gt-customer-table-scroll">
            <table className="gt-customer-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th onClick={() => handleSort('index')}>
                    S No. <SortIcon columnKey="index" />
                  </th>
                  <th onClick={() => handleSort('CustomerCode')}>
                    Customer Code <SortIcon columnKey="CustomerCode" />
                  </th>
                  <th onClick={() => handleSort('CustomerName')}>
                    Customer Name <SortIcon columnKey="CustomerName" />
                  </th>
                  <th>City</th>
                  <th onClick={() => handleSort('SaleId')}>
                    Bill No <SortIcon columnKey="SaleId" />
                  </th>
                  <th>Promo Code</th>
                  <th>Credit Limit</th>
                  <th>Credit Days</th>
                  <th>Bill Date</th>
                  <th>Bill Time</th>
                  <th>Total Sale Qty</th>
                  <th>Total Sale Wt. (KG)</th>
                  <th>Total Sale Amount</th>
                  <th>Total Sale Return Qty</th>
                  <th>Total Sale Return Wt. (KG)</th>
                  <th>Total Sale Return Amount</th>
                  <th>Amt Receipt</th>
                  <th>Balance</th>
                  <th>Bill Cleared</th>
                  <th>Delivery Date</th>
                  <th>Delivery Time</th>
                  <th>Delivery Confirm Date</th>
                  <th>Delivery Confirm Time</th>
                  <th>SR Pickup Date</th>
                  <th>SR Pickup Time</th>
                  <th>SR Approve Date</th>
                  <th>SR Approve Time</th>
                  <th>SR POS Accepted Date</th>
                  <th>SR POS Accepted Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="30" className="gt-customer-loading-container">
                      <CircularProgress />
                    </td>
                  </tr>
                ) : currentRecords.length > 0 ? (
                  currentRecords.map((item, index) => {
                    const actualIndex = indexOfFirstRecord + index;
                    const billingDate = formatDateTime(item.billingDateTime, "date");
                    const billingTime = formatDateTime(item.billingDateTime, "time");
                    const deliveryDate = formatDateTime(item.DeliveryDateTime, "date");
                    const deliveryTime = formatDateTime(item.DeliveryDateTime, "time");
                    const deliveryApproveDate = formatDateTime(item.DeliveryApproveDateTime, "date");
                    const deliveryApproveTime = formatDateTime(item.DeliveryApproveDateTime, "time");
                    const srPickUpDate = formatDateTime(item.SRPickUpDateTime, "date");
                    const srPickUpTime = formatDateTime(item.SRPickUpDateTime, "time");
                    const srApproveDate = formatDateTime(item.SRApproveDateTime, "date");
                    const srApproveTime = formatDateTime(item.SRApproveDateTime, "time");
                    const srAcceptedDate = formatDateTime(item.SRAcceptDateTime, "date");
                    const srAcceptedTime = formatDateTime(item.SRAcceptDateTime, "time");
                    const cleared = item.balance <= 0 ? "Y" : "N";
                    const isExpanded = expandedRows.has(item.SaleId);

                    return (
                      <>
                        <tr key={actualIndex} className="summary-row">
                          <td onClick={() => toggleRow(item.SaleId)} className="expand-cell">
                            <div className="expand-icon">
                              {isExpanded ? '▼' : '▶'}
                            </div>
                          </td>
                          <td className="serial-cell">{actualIndex + 1}</td>
                          <td className="code-cell">{item.CustomerCode || "-"}</td>
                          <td className="customer-name-cell">{item.CustomerName || "-"}</td>
                          <td className="city-cell">{item.City || "-"}</td>
                          <td className="bill-cell">{item.SaleId || "-"}</td>
                          <td className="promo-cell">{item.PromoCodes || "-"}</td>
                          <td className="amount-cell">₹{item.CreditLimit?.toFixed(2) || "0.00"}</td>
                          <td className="days-cell">{item.CreditDays || "0"}</td>
                          <td className="date-cell">{billingDate}</td>
                          <td className="time-cell">{billingTime}</td>
                          <td className="qty-cell sale-qty">{item.TotalQty || "0"}</td>
                          <td className="weight-cell sale-weight">{item.TotalSaleWeight?.toFixed(2) || "0.00"} KG</td>
                          <td className="amount-cell sale-amount">₹{item.TotalAmount?.toFixed(2) || "0.00"}</td>
                          <td className="qty-cell return-qty">{item.SRTotalQty || "0"}</td>
                          <td className="weight-cell return-weight">{item.SRTotalWeight?.toFixed(2) || "0.00"} KG</td>
                          <td className="amount-cell return-amount">₹{item.SRTotalAmount?.toFixed(2) || "0.00"}</td>
                          <td className="amount-cell receipt-amount">₹{item.BillReceipt?.toFixed(2) || "0.00"}</td>
                          <td className={`amount-cell balance-cell ${item.balance > 0 ? 'balance-pending' : 'balance-cleared'}`}>
                            ₹{item.balance.toFixed(2)}
                          </td>
                          <td className={`cleared-cell ${cleared === 'Y' ? 'cleared-yes' : 'cleared-no'}`}>{cleared}</td>
                          <td className="date-cell">{deliveryDate}</td>
                          <td className="time-cell">{deliveryTime}</td>
                          <td className="date-cell">{deliveryApproveDate}</td>
                          <td className="time-cell">{deliveryApproveTime}</td>
                          <td className="date-cell">{srPickUpDate}</td>
                          <td className="time-cell">{srPickUpTime}</td>
                          <td className="date-cell">{srApproveDate}</td>
                          <td className="time-cell">{srApproveTime}</td>
                          <td className="date-cell">{srAcceptedDate}</td>
                          <td className="time-cell">{srAcceptedTime}</td>
                        </tr>
                        {isExpanded && item.items && item.items.map((detailItem, detailIndex) => {
                          const hasReturn = detailItem.SRItemQty > 0 || detailItem.SRItemAmount > 0;
                          return (
                            <tr key={`${actualIndex}-${detailIndex}`} className={`detail-row ${hasReturn ? 'has-return' : ''}`}>
                              <td className="detail-expand"></td>
                              <td className="detail-serial">{detailIndex + 1}</td>
                              <td colSpan="3" className="item-name-detail">
                                <div className="item-detail-container">
                                  <span className="item-icon">📦</span>
                                  <span className="item-name">{detailItem.ItemName || "-"}</span>
                                </div>
                              </td>
                              <td className="batch-detail">
                                <span className="batch-badge">{detailItem.BatchNumber || "-"}</span>
                              </td>
                              <td colSpan="5"></td>
                              <td className="detail-qty sale-detail">{detailItem.ItemQty || "0"}</td>
                              <td className="detail-weight sale-detail">{detailItem.ItemWeight?.toFixed(2) || "0.00"} KG</td>
                              <td className="detail-amount sale-detail">₹{detailItem.ItemTotalAmount?.toFixed(2) || "0.00"}</td>
                              <td className="detail-qty return-detail">{detailItem.SRItemQty || "0"}</td>
                              <td className="detail-weight return-detail">{detailItem.SRItemWeight?.toFixed(2) || "0.00"} KG</td>
                              <td className="detail-amount return-detail">₹{detailItem.SRItemAmount?.toFixed(2) || "0.00"}</td>
                              <td colSpan="13"></td>
                            </tr>
                          );
                        })}
                      </>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="30" className="gt-customer-no-data">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>{`
        /* Pagination Styles */
        .pagination-container {
          margin: 15px 0;
          margin-left: 30px;
        }
        .pagination-button {
          padding: 5px 10px;
          margin: 0 2px;
          border: 1px solid #ccc;
          background: #f5f5f5;
          cursor: pointer;
          color: black;
        }
        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-active {
          background: #004D00;
          color: white;
          border-color: #004D00;
        }
        .data-count {
          margin-left: 15px;
          font-weight: bold;
        }
        .loader-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 20px 0;
        }
        .date-picker-container {
          min-width: 180px;
          margin: 0 5px;
        }
        .customer-date-input-container {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }

        /* Enhanced Summary Row Styles */
        .summary-row {
          background: linear-gradient(to right, #ffffff 0%, #f8f9fa 100%);
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }

        .summary-row:hover {
          background: linear-gradient(to right, #e8f5e9 0%, #c8e6c9 100%);
          border-left: 4px solid #004D00;
          box-shadow: 0 2px 8px rgba(0, 77, 0, 0.15);
          transform: translateX(2px);
        }

        /* Expand Icon Cell */
        .expand-cell {
          cursor: pointer;
          text-align: center;
          background: #e8f5e9;
          border-right: 2px solid #004D00;
        }

        .expand-icon {
          display: inline-block;
          width: 28px;
          height: 28px;
          line-height: 28px;
          border-radius: 50%;
          background: #004D00;
          color: white;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .expand-icon:hover {
          background: #006400;
          transform: scale(1.15);
          box-shadow: 0 2px 8px rgba(0, 77, 0, 0.4);
        }

        /* Cell Type Styles */
        .serial-cell {
          font-weight: 600;
          color: #424242;
          text-align: center;
        }

        .code-cell {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: #004D00;
          font-weight: 600;
        }

        .customer-name-cell {
          font-weight: 600;
          color: #004D00;
          max-width: 200px;
        }

        .city-cell {
          color: #2e7d32;
          font-weight: 500;
        }

        .bill-cell {
          font-family: 'Courier New', monospace;
          font-weight: 700;
          color: #d32f2f;
          background: #ffebee;
          text-align: center;
        }

        .promo-cell {
          font-style: italic;
          color: #f57c00;
        }

        .amount-cell {
          text-align: right;
          font-weight: 600;
          font-family: 'Courier New', monospace;
        }

        .days-cell {
          text-align: center;
          font-weight: 600;
          color: #6a1b9a;
        }

        .date-cell {
          color: #616161;
          font-size: 13px;
        }

        .time-cell {
          color: #757575;
          font-size: 12px;
          font-style: italic;
        }

        /* Quantity and Amount Color Coding */
        .qty-cell {
          text-align: center;
          font-weight: 700;
        }

        .sale-qty {
          color: #2e7d32;
          background: #e8f5e9;
        }

        .sale-amount {
          color: #2e7d32;
          background: #e8f5e9;
        }

        .return-qty {
          color: #c62828;
          background: #ffebee;
        }

        .return-amount {
          color: #c62828;
          background: #ffebee;
        }

        .receipt-amount {
          color: #004D00;
          background: #e8f5e9;
        }

        /* Balance Cell Styles */
        .balance-cell {
          font-weight: 700;
          font-size: 14px;
        }

        .balance-pending {
          color: #d32f2f;
          background: #ffcdd2;
        }

        .balance-cleared {
          color: #388e3c;
          background: #c8e6c9;
        }

        /* Cleared Status */
        .cleared-cell {
          text-align: center;
          font-weight: 900;
          font-size: 14px;
        }

        .cleared-yes {
          color: #388e3c;
          background: #c8e6c9;
        }

        .cleared-no {
          color: #d32f2f;
          background: #ffcdd2;
        }

        /* Detail Row Styles */
        .detail-row {
          background: #f5f5f5;
          border-left: 4px solid #81c784;
          animation: slideIn 0.3s ease-out;
        }

        .detail-row.has-return {
          background: linear-gradient(to right, #fff3e0 0%, #ffe0b2 100%);
          border-left: 4px solid #ff9800;
        }

        .detail-row:hover {
          background: #e8f5e9;
          transform: translateX(4px);
          transition: all 0.2s ease;
        }

        .detail-row.has-return:hover {
          background: linear-gradient(to right, #ffe0b2 0%, #ffcc80 100%);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Detail Cell Styles */
        .detail-expand {
          background: #e8f5e9;
        }

        .detail-serial {
          text-align: center;
          font-weight: 600;
          color: #004D00;
          font-size: 12px;
        }

        .item-name-detail {
          padding-left: 30px !important;
        }

        .item-detail-container {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .item-icon {
          font-size: 18px;
        }

        .item-name {
          font-weight: 700;
          color: #004D00;
          font-size: 14px;
        }

        .batch-detail {
          text-align: center;
        }

        .batch-badge {
          display: inline-block;
          padding: 4px 12px;
          background: linear-gradient(135deg, #004D00 0%, #006400 100%);
          color: white;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 4px rgba(0, 77, 0, 0.3);
        }

        .detail-qty {
          text-align: center;
          font-weight: 700;
          font-size: 13px;
        }

        .detail-amount {
          text-align: right;
          font-weight: 700;
          font-family: 'Courier New', monospace;
          font-size: 13px;
        }

        .sale-detail {
          color: #2e7d32;
        }

        .return-detail {
          color: #c62828;
        }

        /* Table Header Enhancements */
        .gt-customer-table thead th {
          background: linear-gradient(to bottom, #004D00 0%, #003300 100%);
          color: white;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
          border-right: 1px solid rgba(255, 255, 255, 0.1);
          padding: 12px 8px;
          position: sticky;
          top: 0;
          z-index: 10;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .gt-customer-table thead th:hover {
          background: linear-gradient(to bottom, #006400 0%, #004D00 100%);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .date-picker-container {
            width: 100%;
            margin: 5px 0;
          }
          .customer-date-input-container {
            flex-direction: column;
            align-items: stretch;
          }
          .item-name {
            font-size: 12px;
          }
          .batch-badge {
            font-size: 10px;
            padding: 3px 8px;
          }
        }
      `}</style>
    </>
  );
};

export default GTOrderMovementNew;
